// navbar-refactored v1: 重构后的导航栏主组件

import React, { useRef, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';

import { NavbarLogo, ActionButtons, UserDropdown } from './components';
import { useNavbarState, useNavbarActions } from './hooks';

/**
 * 重构后的导航栏组件
 * 使用自定义hooks和组件化的UI元素
 */
const NavBar = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const dropdownRef = useRef(null);

  // 滾動狀態管理
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHomePage, setIsHomePage] = useState(false);

  // 使用自定义hooks管理状态
  const state = useNavbarState(user);

  // 使用自定义hooks管理操作
  const actions = useNavbarActions({
    state,
    setters: {
      setShowDropdown: state.setShowDropdown,
      setUnreadCount: state.setUnreadCount,
    },
    user,
  });

  // 檢測是否在首頁
  useEffect(() => {
    setIsHomePage(location.pathname === '/');
  }, [location.pathname]);

  // 滾動檢測
  useEffect(() => {
    if (!isHomePage) {
      setIsScrolled(false);
      return;
    }

    const handleScroll = () => {
      // 獲取BannerSection元素
      const bannerSection = document.querySelector('[data-banner-section]');
      if (bannerSection) {
        const bannerTop = bannerSection.offsetTop;
        const scrollY = window.scrollY;
        // 當滾動到BannerSection時才切換為黑色導航欄
        setIsScrolled(scrollY >= bannerTop - 100);
      } else {
        // 如果找不到BannerSection，使用原來的邏輯作為後備
        const scrollY = window.scrollY;
        setIsScrolled(scrollY > 300);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  // 当前用户数据（如果已登录）
  const currentUser = user || {
    id: 'guest',
    name: 'Guest',
    avatar: null,
  };

  // 動態樣式計算
  const navClassName =
    isHomePage && !isScrolled
      ? 'fixed top-0 left-0 right-0 bg-transparent z-50 hidden md:block'
      : 'fixed top-0 left-0 right-0 bg-black shadow-sm z-50 hidden md:block';

  // 判斷是否應該使用黑色Logo（白色Logo版本）
  // 如果導航欄背景是黑色，就使用黑色Logo（TAG_Logo_01.png）
  // 邏輯：不是（首頁且未滾動）= 非首頁 或 已滾動
  const shouldUseBlackLogo = !isHomePage || isScrolled;

  return (
    <nav className={navClassName}>
      <div className='w-full px-8 lg:px-12 xl:px-16'>
        <div className='flex justify-between items-center h-16'>
          {/* Logo - Hidden on mobile */}
          <div className='hidden md:block'>
            <NavbarLogo
              onClick={actions.handleLogoClick}
              isScrolled={shouldUseBlackLogo}
            />
          </div>

          {/* Center Upload Button - Absolute Centering - Hidden on mobile */}
          <div className='absolute left-1/2 transform -translate-x-1/2 hidden md:block'>
            <button
              onClick={actions.handleUploadClick}
              className={`bg-transparent px-6 py-3 rounded-full text-sm font-semibold border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                isHomePage && !isScrolled
                  ? 'text-black border-black hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-500 hover:text-white hover:border-transparent'
                  : 'text-white border-white hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-500 hover:border-transparent'
              }`}
              aria-label='Upload Work'
            >
              Upload Work
            </button>
          </div>

          {/* Right Side Buttons */}
          <div className='flex items-center space-x-4'>
            {/* Gallery Button - Same size as TAGMe - Hidden on mobile */}
            <button
              onClick={actions.handleGalleryClick}
              onMouseDown={e => {
                // 中键点击（button === 1）时在新窗口打开主页
                if (e.button === 1) {
                  e.preventDefault();
                  window.open('/', '_blank');
                }
              }}
              className={`bg-transparent px-4 py-2 rounded-md text-sm font-semibold border transition-colors duration-200 min-w-[80px] text-center hidden md:block ${
                isHomePage && !isScrolled
                  ? 'text-black border-black hover:bg-tag-blue hover:border-transparent hover:text-white'
                  : 'text-white border-white/30 hover:bg-tag-blue hover:border-transparent'
              }`}
            >
              Gallery
            </button>

            {/* Project Button - Same size as Gallery - Hidden on mobile */}
            <button
              onClick={actions.handleTAGMeClick}
              className={`bg-transparent px-4 py-2 rounded-md text-sm font-semibold border transition-colors duration-200 min-w-[80px] text-center hidden md:block ${
                isHomePage && !isScrolled
                  ? 'text-black border-black hover:bg-tag-purple hover:border-transparent hover:text-white'
                  : 'text-white border-white/30 hover:bg-tag-purple hover:border-transparent'
              }`}
              aria-label='Project'
            >
              Project
            </button>

            {/* User Dropdown - Hidden on mobile */}
            {!loading && (
              <div className='hidden md:block'>
                <UserDropdown
                  user={currentUser}
                  currentUser={state.currentUser}
                  showDropdown={state.showDropdown}
                  onToggle={actions.handleDropdownToggle}
                  onHover={actions.handleDropdownHover}
                  onLeave={actions.handleDropdownLeave}
                  onSettingsClick={actions.handleSettingsClick}
                  onNotificationsClick={actions.handleNotificationsClick}
                  onLogoutClick={actions.handleLogoutClick}
                  unreadCount={state.unreadCount}
                  dropdownRef={dropdownRef}
                />
              </div>
            )}
            {loading && (
              <div className='w-8 h-8 rounded-full bg-gray-200 animate-pulse'></div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
